import Vue from 'vue';
import types from '../mutation-types';
import ContactAPI from '../../api/contacts';

const state = {
  selectedContacts: {},
  isSelectionMode: false,
  uiFlags: {
    isDeleting: false,
    isLabeling: false,
  },
};

const getters = {
  getSelectedContacts: $state => Object.values($state.selectedContacts),
  getSelectedContactIds: $state =>
    Object.keys($state.selectedContacts).map(Number),
  getSelectedCount: $state => Object.keys($state.selectedContacts).length,
  isContactSelected: $state => id => !!$state.selectedContacts[id],
  getIsSelectionMode: $state => $state.isSelectionMode,
  getUIFlags: $state => $state.uiFlags,
};

const actions = {
  toggleSelectionMode({ commit, state: $state }) {
    const newMode = !$state.isSelectionMode;
    commit(types.SET_CONTACT_SELECTION_MODE, newMode);
    if (!newMode) {
      commit(types.CLEAR_SELECTED_CONTACTS);
    }
  },

  selectContact({ commit }, contact) {
    commit(types.SET_SELECTED_CONTACT, contact);
  },

  deselectContact({ commit }, contactId) {
    commit(types.REMOVE_SELECTED_CONTACT, contactId);
  },

  selectPage({ commit }, contacts) {
    commit(types.SET_SELECTED_CONTACTS_BULK, contacts);
  },

  deselectPage({ commit }, contacts) {
    contacts.forEach(contact => {
      commit(types.REMOVE_SELECTED_CONTACT, contact.id);
    });
  },

  clearSelection({ commit }) {
    commit(types.CLEAR_SELECTED_CONTACTS);
  },

  async bulkDelete({ commit, state: $state, dispatch }) {
    commit(types.SET_CONTACT_SELECTION_UI_FLAG, { isDeleting: true });

    const allIds = Object.keys($state.selectedContacts).map(Number);
    const chunkSize = 50;
    let totalDeleted = 0;
    let totalFailed = 0;
    const allFailedContacts = [];
    const deletedIds = [];

    try {
      for (let i = 0; i < allIds.length; i += chunkSize) {
        const chunk = allIds.slice(i, i + chunkSize);
        const response = await ContactAPI.bulkDelete(chunk);
        const { deleted_count, failed_count, failed_contacts } = response.data;

        totalDeleted += deleted_count;
        totalFailed += failed_count;
        allFailedContacts.push(...(failed_contacts || []));

        const failedIds = new Set(
          (failed_contacts || []).map(fc => fc.id)
        );
        chunk.forEach(id => {
          if (!failedIds.has(id)) {
            deletedIds.push(id);
          }
        });
      }

      const notFoundIds = allFailedContacts
        .filter(fc => fc.reason === 'Not found')
        .map(fc => fc.id);

      [...deletedIds, ...notFoundIds].forEach(id => {
        commit(types.REMOVE_SELECTED_CONTACT, id);
      });

      deletedIds.forEach(id => {
        dispatch('contacts/deleteContactLocally', id, { root: true });
      });

      return { totalDeleted, totalFailed, allFailedContacts };
    } finally {
      commit(types.SET_CONTACT_SELECTION_UI_FLAG, { isDeleting: false });
    }
  },

  async bulkLabel({ commit, state: $state }, labels) {
    commit(types.SET_CONTACT_SELECTION_UI_FLAG, { isLabeling: true });

    const allIds = Object.keys($state.selectedContacts).map(Number);
    const chunkSize = 50;
    let totalLabeled = 0;
    let totalFailed = 0;
    const allFailedContacts = [];

    try {
      for (let i = 0; i < allIds.length; i += chunkSize) {
        const chunk = allIds.slice(i, i + chunkSize);
        const response = await ContactAPI.bulkLabel(chunk, labels);
        const { labeled_count, failed_count, failed_contacts } = response.data;

        totalLabeled += labeled_count;
        totalFailed += failed_count;
        allFailedContacts.push(...(failed_contacts || []));
      }

      return { totalLabeled, totalFailed, allFailedContacts };
    } finally {
      commit(types.SET_CONTACT_SELECTION_UI_FLAG, { isLabeling: false });
    }
  },
};

const mutations = {
  [types.SET_CONTACT_SELECTION_MODE]($state, mode) {
    $state.isSelectionMode = mode;
  },

  [types.SET_SELECTED_CONTACT]($state, contact) {
    Vue.set($state.selectedContacts, contact.id, {
      id: contact.id,
      name: contact.name,
      phone_number: contact.phone_number,
    });
  },

  [types.REMOVE_SELECTED_CONTACT]($state, contactId) {
    Vue.delete($state.selectedContacts, contactId);
  },

  [types.SET_SELECTED_CONTACTS_BULK]($state, contacts) {
    contacts.forEach(contact => {
      Vue.set($state.selectedContacts, contact.id, {
        id: contact.id,
        name: contact.name,
        phone_number: contact.phone_number,
      });
    });
  },

  [types.CLEAR_SELECTED_CONTACTS]($state) {
    $state.selectedContacts = {};
  },

  [types.SET_CONTACT_SELECTION_UI_FLAG]($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
